import { Component, OnInit } from '@angular/core';
import {
  HxCityService,
  HxProductInfoService,
  HxRecipeService,
  ProductInfoModel,
  RecipeModel,
  RecipeRequest,
  RecipeType,
  uiLabel,
  UnitOfMeasureModel,
  WorkflowModel,
  WorkflowStepModel
} from 'hx-services';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxEnumSelectComponent, HxProductInfoSelectComponent, HxUnitMeasureSelectComponent } from 'hx-components';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';

import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NgFor, NgIf } from '@angular/common';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';

@Component({
    selector: 'hc-workflow-recipe-edit',
    templateUrl: './workflow-recipe-edit.component.html',
    styleUrls: ['./workflow-recipe-edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzSkeletonComponent, TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxProductInfoSelectComponent, NzInputDirective, HxUnitMeasureSelectComponent, NzInputGroupComponent, NzSelectComponent, NgFor, NzOptionComponent, HxEnumSelectComponent, NgIf, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, TranslocoPipe]
})
export class WorkflowRecipeEditComponent implements OnInit {
  model!: RecipeModel;
  step!: WorkflowStepModel;
  workflow!: WorkflowModel;
  isEdit = false;
  isCreate = false;
  form!: UntypedFormGroup;

  isLoading = {
    model: false,
  };
  isSaving = false;
  uomName?: string;
  productUomName?: string;
  numbersList: number[] = [];
  RecipeType = RecipeType;

  constructor(
    private aRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private modal: NzModalService,
    private recipeService: HxRecipeService,
    private tr: TranslocoService,
    private cityService: HxCityService,
    private notification: NzNotificationService,
    private router: Router,
    private productInfoService: HxProductInfoService,
  ) {
    this.numbersList = Array.from({length: 100}, (v, k) => k+1);
  }

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as RecipeModel;
    this.step = routeData['step'] as WorkflowStepModel;
    this.workflow = routeData['workflow'] as WorkflowModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.model = {} as RecipeModel;
      this.isCreate = true;
    }
    this.form = this.fb.group({
      productInfoId: [null, [Validators.required]],
      netAmount: [null, [Validators.required]],
      grossAmount: [null, [Validators.required]],
      productUnitOfMeasure: [null, [Validators.required]],
      group: [null, [Validators.required]],
      priority: [null, [Validators.required]],
      coefficient: [null, [Validators.required]],
      toUnitOfMeasure: [null, [Validators.required]],
      type: [null, [Validators.required]]
    });
    this.recipeService.getUnit(this.model.productUnitOfMeasure).then(result => {
      this.productUomName = uiLabel(this.tr.getActiveLang(), result.title);
    });
    this.updateForm();
  }

  async submitForm() {
    console.log('this.form', this.form);
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.getRawValue() as RecipeRequest;
      const model: RecipeRequest = {
        id: this.model.id,
        productInfoId: formModel.productInfoId,
        netAmount: formModel.netAmount,
        grossAmount: formModel.grossAmount,
        productUnitOfMeasure: formModel.productUnitOfMeasure,
        group: formModel.group,
        priority: formModel.priority,
        coefficient: formModel.coefficient,
        toUnitOfMeasure: formModel.toUnitOfMeasure,
        type: formModel.type
      };

      this.isSaving = true;
      try {
        const result = await this.recipeService.saveRecipe(this.step.id, model);
        this.form.markAsPristine();
        if (this.isCreate) {
          this.router.navigate([`/workflows/${this.workflow.id}/steps/${this.step.id}/recipes`]);
          this.notification.success(this.tr.translate('nav.WorkflowRecipes'), this.tr.translate('nt.dataSaved'));
        } else {
          this.notification.success(this.tr.translate('nav.WorkflowRecipes'), this.tr.translate('nt.dataUpdated'));
          this.isLoading.model = true;
          this.model = await this.recipeService.getRecipeById(result.id);
          this.isLoading.model = false;
        }
      } catch (err: any) {
        this.notification.error(this.tr.translate('nav.WorkflowRecipes'), this.tr.translate(err?.error?.message));
      } finally {
        this.isSaving = false;
      }
    } else {
      console.log('not valid form', this.form.errors);
    }
  }

  onUomChanged(ev: UnitOfMeasureModel | UnitOfMeasureModel[] | undefined) {
    if (ev && !Array.isArray(ev)) {
      this.uomName = uiLabel(this.tr.getActiveLang(), ev.title);
    }
  }

  onProductChanged(ev: ProductInfoModel | ProductInfoModel[] | undefined) {
    console.log('ev=', ev);
    if (ev) {
      if (!Array.isArray(ev)) {
        this.productInfoService.getProductInfo(ev.id).subscribe(result => {
          if (result && result.unitOfMeasureId) {
            this.getUnitOfMeasureById(result.unitOfMeasureId);
          }
        });
      }
    } else {
      this.uomName = undefined;
      this.productUomName = undefined;
      this.form.controls['productUnitOfMeasure'].reset();
    }
  }

  private getUnitOfMeasureById(id: number) {
    this.recipeService.getUnit(id).then(result => {
      this.form.controls['productUnitOfMeasure'].patchValue(result.id);
      this.productUomName = uiLabel(this.tr.getActiveLang(), result.title);
    });
  }

  private updateForm() {
    const formModel: Partial<RecipeRequest> = {
      productInfoId: this.model.productInfoId,
      netAmount: this.model.netAmount,
      grossAmount: this.model.grossAmount,
      productUnitOfMeasure: this.model.productUnitOfMeasure,
      group: this.model.group,
      priority: this.model.priority,
      coefficient: this.model.coefficient,
      toUnitOfMeasure: this.model.toUnitOfMeasure,
      type: this.model.type
    };
    this.form.patchValue(formModel);
  }
}
